import { IEventActivityInterface } from '../models/event-activity/event-activity.interface'
import { getEventActivities } from '../models/event-activity/event-activity.helper'
import moment from 'moment'
import { useInfiniteQuery } from '@tanstack/react-query'

export interface EventActivity extends IEventActivityInterface {
  time: string
  date: string
  actionNgrams?: string[]
}
/**
 * Custom hook to manage event activities
 * @param eventId The ID of the event to fetch activities for
 * @param limit The number of activities to fetch per page
 * @param searchTerm The search term to filter activities
 * @returns Object containing activities, loading state, and methods to interact with activities
 */
export const useEventActivities = (eventId: string, limit = 20, searchTerm: string = '') => {
  // Use infinite query for pagination
  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery(
    {
      queryKey: ['eventActivities', eventId, limit, searchTerm],
      queryFn: async ({ pageParam }) => {
        if (!eventId) return { activities: [], lastDoc: null }

        const result = await getEventActivities(eventId, limit, pageParam, searchTerm)

        return result
      },
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (lastPage.activities.length < limit) return undefined
        return lastPage.lastDoc
      },
      enabled: !!eventId,
    }
  )

  // Transform the infinite query data into a flat array of formatted activities
  const activities =
    data?.pages
      .flatMap((page) => page.activities)
      .map((activity) => {
        const timestamp =
          activity.activityTime instanceof Date
            ? activity.activityTime
            : new Date(activity.activityTime as string)

        return {
          ...activity,
          time: moment(timestamp).format('hh:mm a'),
          date: moment(timestamp).format('DD MMM, YYYY'),
        }
      }) || []

  return {
    activities,
    loading: isLoading,
    error: isError ? error : null,
    canLoadMore: !!hasNextPage,
    loadMore: () => fetchNextPage(),
    refresh: () => refetch(),
  }
}
